import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes/platform.class';
import { ConfirmationDialogComponent, DeprecatedConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { Salary } from '@app/models/employee/salary.model';
import { AnalyticService, PayrollService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { StoreView } from '@common/views';
import { EmployeesResources } from '@employees/employees.resources';
import { AssignTaskTemplatesService, EmployeeRoeService } from '@employees/services';
import { TerminationService } from '@employees/services/termination.service';
import { TerminateInformationForm } from '@forms/employees/components';
import { Employee } from '@models/employee/employee.model';
import { OffboardingReminder } from '@models/employee/offboarding-reminder.model';
import { TaskTemplate } from '@models/tasks/task-template.model';
import moment from 'moment';

@Component({
    templateUrl: './employee-terminate.template.html',
    styleUrls: ['./employee-terminate.style.scss'],
})
export class EmployeeTerminateView extends StoreView implements OnInit {
    @ViewChild('confirmGenerateRoeReminder', { static: true })
    confirmGenerateRoeReminder: DeprecatedConfirmationDialogComponent;

    @ViewChild('informationForm', { static: true }) informationForm: TerminateInformationForm;
    @ViewChild('nextStepsModal') nextStepsModal: ConfirmationDialogComponent;

    isLoading = false;
    employee: Employee;
    reminders: OffboardingReminder[] = [];
    offboardingTaskTemplates: TaskTemplate[] = [];
    showBenefitExtensionOption = false;
    benefitExtensionDate: Date | null = null;
    hasManagedBenefitPolicy = false;
    lastDayOfWorkOn: Date | null = null;
    showBackDatedModal = false;
    lastDayOfWorkIsBackDated = false;
    backDatedTerminationDateAffirmed = false;
    lastPaidRecurringPayrollEndDate: Date | null = null;
    hasSimplyBenefitsFlag = false;
    employeeIsOnTimeTracking = false;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private auth: AuthService,
        private notify: NotifyService,
        private http: HttpClient,
        private terminationService: TerminationService,
        private employeeRoeService: EmployeeRoeService,
        private analyticService: AnalyticService,
        private assignTaskTemplatesService: AssignTaskTemplatesService,
        private payrollService: PayrollService,
        private featureService: FeatureService
    ) {
        super();

        this.employee = this.route.snapshot.data.models.get('employee');
        this.employeeIsOnTimeTracking = this.employee.hasModule(Platform.modules.timeTracking);
    }

    async ngOnInit(): Promise<void> {
        this.hasSimplyBenefitsFlag = await this.featureService.has(FeatureFlag.simplyBenefits);

        this.hasManagedBenefitPolicy =
            this.employee.hasBenefitPlan() &&
            this.employee.benefitPlans.filter(
                (benefitPlan) =>
                    benefitPlan.benefitPlan?.benefitPolicy?.conversionEmail ||
                    benefitPlan.benefitPlan?.hasEnhancedManagedBenefits
            ).length > 0;

        if (this.hasManagedBenefitPolicy) {
            this.showBenefitExtensionOption = true;
        }

        if (this.hasSimplyBenefitsFlag && this.employee.hasSimplyBenefits) {
            this.showBenefitExtensionOption = true;
        }
    }

    async findEmployeesMostCurrentPrimaryCompensation(): Promise<Salary> {
        const [employeeSalaries] = await Salary.param('company', this.auth.company.id)
            .param('employee', this.employee.id)
            .orderBy('effectiveAt', 'DESC')
            .all();
        const [latestPrimaryCompensation] = employeeSalaries.filter(
            (compensation: Salary) => compensation?.isPrimary && compensation?.status === 'current'
        );
        return latestPrimaryCompensation;
    }

    async onSave(): Promise<void> {
        if (this.isLoading) {
            return;
        }

        // This is a sanity check. The UI will not allow these to be null before saving, but TypeScript doesn't know this.
        if (!this.employee.terminationReasonCode || !this.employee.terminationComments) {
            // This error should be impossible to reach, but let's notify just in case.
            this.notify.error('error');
            return;
        }

        this.isLoading = true;

        if (this.employee.isPayrollSyncEnabled) {
            // Show next steps for payroll employees
            const confirm = await this.nextStepsModal.open();

            if (!confirm) {
                this.isLoading = false;
                return;
            }
        }

        try {
            await this.terminationService.terminate(
                this.employee,
                moment(this.employee.lastDayOfWorkOn).toDate(),
                this.employee.terminationReasonCode,
                this.employee.terminationComments,
                this.benefitExtensionDate
            );

            if (this.reminders.length) {
                await this.attachReminders(this.employee);
            }

            if (this.offboardingTaskTemplates.length) {
                await this.assignOffboardingTasks();
            }

            // track before ROE as it can navigate away
            this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileMeatballTerminate);

            /**
             * TODO: promptRoeDecision can redirect, this causes bugs in termination flow. Needs a refactor to not
             * effect user flow, or show notifications etc.
             *
             * For now - keep it as the last action in termination flow.
             */
            if (this.shouldGenerateRoe(this.employee)) {
                // @todo refactor
                // any response to the prompt will navigate
                this.promptRoeDecision(this.employee);
            }

            this.notify.success('employees.terminate.employeeTerminated');
            this.router.navigate(['/', 'employees', 'directory', this.employee.id]);
        } catch (e) {
            this.notify.error(e);
            this.isLoading = false;
        }
    }

    shouldMoveToNextStep = async (): Promise<void> => {
        if (this.employee.isPayrollSyncEnabled) {
            this.lastPaidRecurringPayrollEndDate = await this.payrollService.getLastRecurringPayrollEndDate(
                this.auth.company.id
            );

            this.lastDayOfWorkIsBackDated =
                this.lastPaidRecurringPayrollEndDate !== null
                    ? moment(this.lastDayOfWorkOn).isBefore(this.lastPaidRecurringPayrollEndDate)
                    : false;
            if (this.lastDayOfWorkIsBackDated && !this.backDatedTerminationDateAffirmed) {
                this.showBackDatedModal = true;
                return new Promise<void>(() => {
                    return Promise.reject();
                });
            }
        }

        return new Promise<void>((resolve) => {
            resolve();
        });
    };

    shouldGenerateRoe(employee: Employee): boolean {
        const payrollModule = this.auth.company.modules.find((module) => module.name === 'Payroll');
        if (!payrollModule) {
            return false;
        }

        return employee.activeOnPayroll() && this.auth.can(Platform.payroll.permission.view);
    }

    promptRoeDecision(employee: Employee): void {
        this.confirmGenerateRoeReminder.show().then((generateLater) => {
            if (!generateLater) {
                // @todo refactor
                // On success navigates to the new ROE
                this.employeeRoeService.issueRoe(employee);
                return;
            }

            // @todo refactor
            // On success navigates to people directory
            this.employeeRoeService
                .generateReminder(employee, employee.lastDayOfWorkOn)
                .then(() => {
                    this.router.navigate(['/', 'employees', 'directory']);
                })
                .catch((err) => {
                    this.notify.error(err);
                    this.isLoading = false;
                });
        });
    }

    private async attachReminders(employee: Employee): Promise<void> {
        await this.http
            .post(EmployeesResources.AssignOffboardingReminders.replace(':company', this.auth.company.id.toString()), {
                data: {
                    type: 'reminders',
                    attributes: {
                        offboardingReminderIds: this.reminders.map((reminder) => reminder.id),
                        employeeId: employee.id,
                    },
                },
            })
            .toPromise();
    }

    private async assignOffboardingTasks(): Promise<void> {
        this.analyticService.trackEvent(AnalyticEvents.AddTasksDuringTerminationFlow);

        this.isLoading = true;
        try {
            await this.assignTaskTemplatesService.onSave(this.offboardingTaskTemplates, [this.employee]);

            this.notify.success('employees.terminate.assignedTasks');
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }
}
